import _ from 'lodash'
import { isItemVisibleByLogic, isAnswerGiven } from '@/utils/formVisibility'
import { normalizeCheckitemValue } from '@/utils/answerOptions'
import {
  getItemObtainedPoints,
  getItemPossiblePoints,
  getAnsweredItemObtainedPoints,
  getLogicPropertyItemIds,
  getLogicItemsRoutingToVisibleGroup,
  normalizeItemObtainedPoints,
  getObtainedPointsByGroup,
  getNAPointsByGroup,
  getPossiblePointsByGroup,
  getClosedOccurrenceScore,
  getOccurrenceScore,
  getSelfEvaluationResult,
  getTablesDifferenceSum,
  getExtraPropertiesAnswerSum
} from '@/utils/scoring'
import { safeExtraPropertiesSum } from '@/offline/sync/builders/extra-properties'

// Items visibles segun la logica condicionada de sus grupos: la parte comun de currentFormGroups y
// currentFormGroupById. Recibe los valores ya leidos de los getters (puntos y respuestas por item).
function visibleFormItems (items, obtainedPointsByItem, answersByItem) {
  const obtainedPointsMap = _.keyBy(obtainedPointsByItem, 'id')
  return items.filter((item) => isItemVisibleByLogic(
    item,
    items,
    (q) => obtainedPointsMap[q.id]?.obtained_points,
    (q) => isAnswerGiven(answersByItem[q.id])
  ))
}

export default {
  addons: (state) => state.current ? state.current.addons : {},
  currentFormItems: (state) => state.current ? state.current.form.items : [],
  currentAnswersByItem (state) {
    const answers = state.current ? state.current.occurrence_answers : []
    // TKT-2378: un check guardado como "1" (formato de offline-v2) se leia como "sin responder" al
    // reabrir: la casilla quedaba vacia, el grupo sumaba 0 y el score del cierre salia sin esos puntos.
    const checkitemIds = new Set(
      (state.current?.form?.items || []).filter(item => item.type === 'form-checkitem').map(item => item.id)
    )
    return _.keyBy(
      (answers || []).map(answer => checkitemIds.has(answer.item_id)
        ? { ...answer, value: normalizeCheckitemValue(answer.value) }
        : answer),
      'item_id'
    )
  },
  addonsByItem (state) {
    if (!state.current || !state.current.addons) return {}

    const filteredAddons = {}
    // Recorrer cada propiedad del objeto addons
    for (const itemId in state.current.addons) {
      const currentAddons = state.current.addons
      if (Object.prototype.hasOwnProperty.call(currentAddons, itemId)) {
        // Filtrar los elementos del arreglo con el tipo "document"
        const filteredItems = state.current.addons[itemId].filter(item => item.type === 'document')

        // Agregar el arreglo filtrado al objeto filteredAddons si no está vacío
        if (filteredItems.length > 0) filteredAddons[itemId] = filteredItems
      }
    }
    return filteredAddons
  },
  currentFormGroups () {
    const obtainedPointsByItem = this.currentObtainedPointsByItem
    const answersByItem = this.currentAnswersByItem

    const formItems = visibleFormItems(this.currentFormItems, obtainedPointsByItem, answersByItem)

    return _.groupBy(formItems, 'group_id')
  },
  currentFormGroupById () {
    const Ids = []
    const obtainedPointsByItem = this.currentObtainedPointsByItem
    const answersByItem = this.currentAnswersByItem

    const formItems = visibleFormItems(this.currentFormItems, obtainedPointsByItem, answersByItem)

    formItems.forEach(item => {
      Ids.push(item.group.id)
    })

    return [...new Set(Ids)]
  },
  currentObtainedPointsByItem () {
    const items = this.currentFormItems.map(item => {
      item.obtained_points = getAnsweredItemObtainedPoints(item, this.currentAnswersByItem[item.id])

      return {
        id: item.id,
        group_id: item.group_id,
        obtained_points: isNaN(item.obtained_points)
          ? item.obtained_points
          : Number(item.obtained_points)
      }
    })
    return items
  },
  currentValue () {
    const items = this.currentFormItems.map(item => {
      if (typeof this.currentAnswersByItem[item.id] !== 'undefined') {
        return { value: this.currentAnswersByItem[item.id].value }
      } else {
        return { value: null }
      }
    })
    return items
  },
  logicPropertyItemIds () {
    return getLogicPropertyItemIds(this.currentFormItems)
  },
  // Items whose currently selected answer matches at least one of their logic rules
  // (i.e. the answer leads to a target group). Items whose answer leads to no group
  // are treated as regular scored questions so the form can be marked as failed.
  logicItemsRoutingToVisibleGroup () {
    return getLogicItemsRoutingToVisibleGroup(this.currentFormItems, this.currentObtainedPointsByItem)
  },
  normalizeObtainedPoints () {
    return (item) => normalizeItemObtainedPoints(item, {
      isLogicItem: (itemId) => this.logicPropertyItemIds.has(itemId),
      routesToVisibleGroup: (itemId) => this.logicItemsRoutingToVisibleGroup.has(itemId),
      hasAnswer: (itemId) => Boolean(this.currentAnswersByItem[itemId])
    })
  },
  currentObtainedPointsByGroup () {
    return getObtainedPointsByGroup(this.currentObtainedPointsByItem, item => this.normalizeObtainedPoints(item))
  },
  itemObtainedPoints () {
    return itemId => _.find(this.currentObtainedPointsByItem, { id: itemId }).obtained_points
  },
  itemPossiblePoints () {
    return itemId => _.find(this.currentPossiblePointsByItem, { id: itemId }).possible_points || 'na'
  },
  itemExtraProperties () {
    return itemId => this.currentAnswersByItem[itemId] ? this.currentAnswersByItem[itemId].extra_properties : []
  },
  currentPossiblePointsByItem () {
    const items = this.currentFormItems.map(item => {
      item.possible_points = getItemPossiblePoints(item, this.currentObtainedPointsByItem)

      return {
        id: item.id,
        group_id: item.group_id,
        possible_points: Number(item.possible_points)
      }
    })
    return items
  },
  currentNAPointsByGroup () {
    return getNAPointsByGroup(
      this.currentObtainedPointsByItem,
      item => this.normalizeObtainedPoints(item),
      itemId => this.logicPropertyItemIds.has(itemId),
      () => this.currentPossiblePointsByItem
    )
  },
  currentPossiblePointsByGroup () {
    return getPossiblePointsByGroup(
      this.currentPossiblePointsByItem,
      itemId => this.logicPropertyItemIds.has(itemId),
      itemId => this.logicItemsRoutingToVisibleGroup.has(itemId)
    )
  },
  currentScore (state) {
    if (state.current && state.current.is_closed) {
      return getClosedOccurrenceScore(state.current.score)
    }

    const formGroups = this.currentFormGroups
    const obtainedPointsByGroup = this.currentObtainedPointsByGroup
    const possiblePointsByGroup = this.currentPossiblePointsByGroup
    const naPointsByGroup = this.currentNAPointsByGroup

    return getOccurrenceScore({
      formGroups,
      obtainedByGroup: obtainedPointsByGroup,
      possibleByGroup: possiblePointsByGroup,
      naByGroup: naPointsByGroup,
      items: this.currentFormItems
    })
  },
  currentSelfEvaluationResult () {
    this.currentFormItems.forEach(item => {
      item.obtained_points = this.currentAnswersByItem[item.id]
        ? getItemObtainedPoints(item, this.currentAnswersByItem[item.id].value)
        : 0
    })

    return getSelfEvaluationResult(this.currentFormItems)
  },
  extraPropertiesSum (state) {
    let sum = 0
    if (state.current) {
      const items = state.current.form.items
      const getAnswersByItem = () => this.currentAnswersByItem

      sum = getTablesDifferenceSum(items, getAnswersByItem)

      items.filter(item => item.extra_properties && item.extra_properties.length > 0).forEach(item => {
        const forResult = item.extra_properties.filter(extra_property => extra_property.sum_for_result)[0]
        if (forResult) forResult.item_id = item.id
      })

      sum = getExtraPropertiesAnswerSum(items, getAnswersByItem, sum)
    }
    return sum
  },
  extraPropertiesSumPayload () {
    return safeExtraPropertiesSum(() => this.extraPropertiesSum)
  },
  missingSignatureWarningKey (state) {
    const assignment = state.current?.assignment
    if (!assignment) return null
    const signatureCount = (state.current.signatures || []).length

    if (assignment.signature_check == 1 && signatureCount === 0) {
      return 'occurrences.labels.first_signature_warning'
    }
    if (assignment.aditional_signature_check == 1 && signatureCount < 2) {
      return 'occurrences.labels.second_signature_warning'
    }
    if (
      assignment.additional_signatures_required == 1 &&
      assignment.signature_roles?.length > 0 &&
      signatureCount < (assignment.signature_roles.length + 2)
    ) {
      return 'occurrences.labels.additional_signature_warning'
    }
    return null
  }
}
